/**
 * 全局待办联动：App/PC 任一端变更 → 各角色角标与待办箱同步增减
 * - 2s 拉 yn_todo_pulse（count+rev）
 * - rev 变化才通知订阅者，避免无意义刷屏
 */
import api from '@/utils/api'
import { fetchTodoRows, fetchTodoFlatRows } from '@/utils/todoAdapter'

const POLL_MS = 2000
const EVENT = 'yn-todo-sync'

let started = false
let timer = null
let lastRev = ''
let lastCount = 0
let inflight = false
let listeners = 0

function parsePulse(res) {
  let d = res && res.data
  if (typeof d === 'string') {
    try {
      d = JSON.parse(d.trim())
    } catch (e) {
      d = {}
    }
  }
  d = d || {}
  return {
    ok: d.ok !== 0 && !d.error,
    count: Number(d.count) || 0,
    rev: String(d.rev || ''),
    pickAudit: Number(d.pickAudit) || 0,
    handover: Number(d.handover) || 0,
    entrustAudit: Number(d.entrustAudit) || 0
  }
}

function emit(payload) {
  try {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: payload }))
  } catch (e) { /* ignore */ }
}

async function tick(force) {
  if (inflight) return
  if (!force && typeof document !== 'undefined' && document.visibilityState === 'hidden') return
  inflight = true
  try {
    const res = await api.get('yn_todo_pulse')
    const pulse = parsePulse(res)
    if (!pulse.ok) return
    const changed = force || pulse.rev !== lastRev || pulse.count !== lastCount
    if (!changed) return
    const prevCount = lastCount
    lastRev = pulse.rev
    lastCount = pulse.count
    emit({
      ...pulse,
      prevCount,
      delta: pulse.count - prevCount,
      force: !!force
    })
  } catch (e) {
    /* 静默：断网不打断业务页 */
  } finally {
    inflight = false
  }
}

function onVis() {
  if (document.visibilityState === 'visible') tick(true)
}

function onBus() {
  tick(true)
}

function onStorage(e) {
  if (e && e.key === 'yn_todo_ping') tick(true)
}

function ensureStarted() {
  if (started) return
  started = true
  tick(true)
  timer = setInterval(() => tick(false), POLL_MS)
  document.addEventListener('visibilitychange', onVis)
  window.addEventListener('focus', onBus)
  window.addEventListener('yn-todo-refresh', onBus)
  window.addEventListener('storage', onStorage)
}

function maybeStop() {
  if (listeners > 0 || !started) return
  started = false
  if (timer) {
    clearInterval(timer)
    timer = null
  }
  document.removeEventListener('visibilitychange', onVis)
  window.removeEventListener('focus', onBus)
  window.removeEventListener('yn-todo-refresh', onBus)
  window.removeEventListener('storage', onStorage)
}

/** 订阅待办变化；返回取消函数 */
export function subscribeTodoSync(handler) {
  if (typeof handler !== 'function') return () => {}
  const wrap = (e) => handler((e && e.detail) || {})
  window.addEventListener(EVENT, wrap)
  listeners += 1
  ensureStarted()
  // 立刻给一帧当前值（若已有）
  if (lastRev) {
    try {
      handler({
        count: lastCount,
        rev: lastRev,
        prevCount: lastCount,
        delta: 0,
        force: false
      })
    } catch (e) { /* ignore */ }
  }
  return () => {
    window.removeEventListener(EVENT, wrap)
    listeners = Math.max(0, listeners - 1)
    maybeStop()
  }
}

/** 业务写操作后立刻催一次（本页 + 多标签） */
export function pingTodoRefresh() {
  try {
    window.dispatchEvent(new CustomEvent('yn-todo-refresh'))
    localStorage.setItem('yn_todo_ping', String(Date.now()))
  } catch (e) { /* ignore */ }
  tick(true)
}

export function getTodoSyncSnapshot() {
  return { count: lastCount, rev: lastRev }
}

export { fetchTodoRows, fetchTodoFlatRows, EVENT as TODO_SYNC_EVENT, POLL_MS }
